import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	computed,
	inject,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { BusinessPartner, CountryPhoneConfiguration } from '@oe-backend/types';
import {
	ButtonComponent,
	InputFeatureDirective,
	TextInputComponent,
} from '@zeiss/beyond-online-angular';
import { BehaviorSubject, filter, firstValueFrom, lastValueFrom } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import { Suggestion } from 'src/app/types/interfaces/order-address-suggestions.interface';
import { FormControlWrapper } from 'src/app/types/order.type';
import {
	AutoCompleteItem,
	AutoCompleteModule,
	AzmAddressAutosuggestModule,
	IconComponent,
	PopUpService,
	SortPipe,
	ToastService,
	ToastType,
} from 'vis-ods-angular';
import { Address } from 'vis-ods-angular/lib/modules/azm-address-autosuggest/interfaces';
import { AddressValidationComponent } from './address-validation/address-validation.component';

export interface DirectShipmentFormValues
	extends Required<
		Omit<
			BusinessPartner,
			| 'agreementId'
			| 'customerNumber'
			| 'region'
			| 'phonePrefix'
			| 'secondLineName'
			| 'thirdLineName'
			| 'fourthLineName'
			| 'salesOrgId'
			| 'legalName'
			| 'streetPrefixName'
			| 'contactPerson'
		>
	> {
	//Workaround for the optional phonePrefix in the MDSAddress type which can't be set to null
	phonePrefix?: CountryPhoneConfiguration;
}

@Component({
	selector: 'app-customer-address',
	templateUrl: './customer-address.component.html',
	imports: [
		TranslocoModule,
		ReactiveFormsModule,
		AutoCompleteModule,
		SortPipe,
		IconComponent,
		ButtonComponent,
		TextInputComponent,
		AzmAddressAutosuggestModule,
		InputFeatureDirective,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerAddressComponent implements OnInit {
	@Output() addressChanged = new EventEmitter<BusinessPartner>();
	@Output() cancel = new EventEmitter<boolean>();

	@Input() initialShipToAddress?: BusinessPartner;

	public $formattedCountryCodes = computed<AutoCompleteItem<string>[]>(() => {
		const countryCodes: string[] = [];

		this._$countryPhoneCodes().forEach((countryPhoneCode) => {
			if (
				countryPhoneCode.countryCode &&
				countryCodes.includes(countryPhoneCode.countryCode) === false
			) {
				countryCodes.push(countryPhoneCode.countryCode);
			}
		});

		return countryCodes.map((countryCode) => {
			return {
				value: countryCode,
				displayValue: this.getTranslatedCountryName(countryCode),
			};
		});
	});
	public $formattedCountryPhoneCodes = computed<AutoCompleteItem<CountryPhoneConfiguration>[]>(() =>
		this._$countryPhoneCodes()
			.map((phoneCode) => {
				return {
					value: phoneCode,
					displayValue: `${phoneCode.phonePrefix} (${this.getTranslatedCountryName(
						phoneCode.countryCode!,
					)})`,
				};
			})
			.sort((a, b) => (a.displayValue < b.displayValue ? -1 : 1)),
	);
	public customerAddressForm = new FormGroup<FormControlWrapper<DirectShipmentFormValues>>({
		firstLineName: new FormControl('', [Validators.required]),
		// name2: new FormControl('', [Validators.required]),
		street: new FormControl('', [Validators.required]),
		houseNumber: new FormControl('', [Validators.required]),
		city: new FormControl('', [Validators.required]),
		state: new FormControl(''),
		zipCode: new FormControl('', [Validators.required]),
		countryCode: new FormControl('', Validators.required),
		phonePrefix: new FormControl<CountryPhoneConfiguration | undefined>(undefined, {
			nonNullable: true,
			validators: [Validators.required],
		}),
		postBox: new FormControl(''),
		floor: new FormControl<number | null>(null),
		room: new FormControl<number | null>(null),
		email: new FormControl('', [
			Validators.required,
			Validators.pattern(/^[a-z0-9!#$%&'*+-/=?^_`{|}~]+@[a-z0-9.-]+\.[a-z]{2,4}$/),
		]),
		phone: new FormControl('', [Validators.required, Validators.pattern(/^(?:\d[-\s]?){1,12}\d$/)]),
	});
	public $isValidating = signal(false);
	public userLanguage = 'en-US';
	public addressSuggestControl = new FormControl<Address | null>(null);

	private readonly userDataService = inject(UserDataService);
	private readonly translocoService = inject(TranslocoService);
	private readonly popUpService = inject(PopUpService);
	private readonly toastService = inject(ToastService);
	private readonly apiService = inject(ApiService);
	private _$countryPhoneCodes = signal<CountryPhoneConfiguration[]>([]);

	constructor() {
		this.customerAddressForm.controls.countryCode.valueChanges
			.pipe(
				takeUntilDestroyed(),
				filter(() => this.customerAddressForm.controls.phonePrefix!.untouched),
			)
			.subscribe((newValue) => {
				this.setPhonePrefixFromCountryCode(newValue);
			});
	}

	async ngOnInit() {
		this.userLanguage = this.userDataService.getUserLanguage() || 'en-US';
		this._$countryPhoneCodes.set(await lastValueFrom(this.apiService.getCountryPhoneCodes()));

		if (this.initialShipToAddress) {
			this.customerAddressForm.setValue(
				this.createFormValuesFromShippingAddress(this.initialShipToAddress),
			);
		} else {
			this.customerAddressForm.controls.countryCode.setValue(
				this.userDataService.getUserCountry() || 'US',
			);
		}
	}

	searchFn = (term: string, item: AutoCompleteItem<any>) => {
		return item.displayValue.toLowerCase().includes(term.toLowerCase());
	};

	comparePhonePrefixFn = (
		item: AutoCompleteItem<CountryPhoneConfiguration>,
		selected: CountryPhoneConfiguration,
	) => {
		return (
			item.value.countryCode === selected.countryCode &&
			item.value.phonePrefix === selected.phonePrefix
		);
	};

	getError(control: AbstractControl, controlName: string): string | boolean {
		if (control.touched && control.errors) {
			if (control.errors['required']) {
				return this.translocoService.translate(
					'generalComponents.addressSelector.subComponents.customerAddress.texts.required',
				);
			}

			if (control.errors?.['pattern']) {
				return this.translocoService.translate(
					`generalComponents.addressSelector.subComponents.customerAddress.inputs.${controlName}.errors.invalid`,
				);
			}
		}
		return false;
	}

	async onAccept() {
		if (this.customerAddressForm.valid) {
			this.$isValidating.set(true);
			try {
				const result = await lastValueFrom(
					this.apiService.validateShipToAddress(this.customerAddressForm.value),
				);
				if (result.results.length > 1) {
					const selectedSuggestion = await this.displayAddressSuggestionPopUp(result.results);
					if (selectedSuggestion) {
						this.customerAddressForm.patchValue(
							this.getShipmentAddressFromSuggestion(selectedSuggestion),
						);
						this.customerAddressForm.updateValueAndValidity();
						if (this.customerAddressForm.valid) {
							this.addressChanged.emit(this.customerAddressForm.value);
						}
					}
				} else {
					this.addressChanged.emit(this.customerAddressForm.value);
				}
			} catch {
				this.toastService.addToast({
					automaticDeleteTime: 20000,
					variant: ToastType.error,
					headline: this.translocoService.translate('errors.InvalidAddress.headline'),
					description: this.translocoService.translate('errors.InvalidAddress.text'),
				});
			}
			this.$isValidating.set(false);
		} else {
			this.customerAddressForm.markAllAsTouched();
			this.customerAddressForm.updateValueAndValidity();
			this.scrollToFirstInvalidControl();
		}
	}

	onCancel() {
		this.cancel.emit(true);
	}

	onAddressSelected(address: Address | null) {
		if (address) {
			this.customerAddressForm.patchValue({
				street: address.streetName || '',
				houseNumber: address.streetNumber || '',
				zipCode: address.postalCode || '',
				city: address.municipality || '',
				state: '',
				floor: null,
				room: null,
				postBox: '',
			});
		}
	}

	private createFormValuesFromShippingAddress(address: BusinessPartner): DirectShipmentFormValues {
		return {
			firstLineName: address.firstLineName || '',
			street: address.street || '',
			houseNumber: address.houseNumber || '',
			city: address.city || '',
			state: address.state || '',
			zipCode: address.zipCode || '',
			countryCode: address.countryCode || '',
			postBox: address.postBox || '',
			floor: address.floor || null,
			room: address.room || null,
			email: address.email || '',
			phone: address.phone || '',
			phonePrefix: address.phonePrefix || undefined,
		};
	}

	private setPhonePrefixFromCountryCode(countryCode: string | null) {
		this.customerAddressForm.controls.phonePrefix!.setValue(
			this._$countryPhoneCodes().find((phoneCode) => phoneCode.countryCode === countryCode) ||
				undefined,
		);
	}

	private getTranslatedCountryName(countryCode: string): string {
		const regionNames = new Intl.DisplayNames([this.userLanguage || 'en'], { type: 'region' });
		const countryName = regionNames.of(countryCode);
		return countryName || countryCode;
	}

	private scrollToFirstInvalidControl() {
		const firstInvalidElement = document.querySelector('[data-invalid="true"]');
		if (firstInvalidElement) {
			firstInvalidElement.scrollIntoView({ behavior: 'smooth' });
		}
	}

	private displayAddressSuggestionPopUp(suggestions: Suggestion[]): Promise<Suggestion | null> {
		const popUpRef = this.popUpService.open(AddressValidationComponent, {
			enableCloseButton: new BehaviorSubject<boolean>(false),
			disableBackdropClose: new BehaviorSubject<boolean>(true),
			containerClasses: ['!max-w-[500px]'],
			data: {
				addressSuggestions: suggestions,
			},
		});
		return firstValueFrom(popUpRef.afterClosed);
	}

	private getShipmentAddressFromSuggestion(suggestion: Suggestion): BusinessPartner {
		const formValues = this.customerAddressForm.value;
		return {
			...formValues,
			street: suggestion.address.streetName || '',
			houseNumber: suggestion.address.streetNumber || '',
			city: suggestion.address.localName || '',
			zipCode: suggestion.address.postalCode || '',
			countryCode: suggestion.address.countryCode || '',
			state: suggestion.address.countrySubdivision || '',
		};
	}
}
